import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Loader2,
  Globe,
  MessageSquare,
  Users,
  Video,
  FileText,
} from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/common/Logo';

function getSafeRedirect(redirectParam: string | null): string {
  if (!redirectParam) {
    return '/app';
  }

  // Only permit internal relative paths starting with a single slash.
  // This prevents open-redirect URLs such as //evil.com.
  if (
    redirectParam.startsWith('/') &&
    !redirectParam.startsWith('//')
  ) {
    return redirectParam;
  }

  return '/app';
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect');
  const emailParam = searchParams.get('email') || '';

  const { login } = useAuth();

  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle login form submission.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const res = await api.auth.login({
        email: email.trim().toLowerCase(),
        password,
      });

      await login({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
      });

      navigate(getSafeRedirect(redirectParam));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
        'Invalid email or password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fill the configured demo account credentials.
   */
  const handleDemoLogin = () => {
    setEmail('staff.architect@sahajconnect.com');
    setPassword('Password123!');
    setError(null);
  };

  /**
   * Build the registration URL while preserving redirect/email params.
   */
  const getRegisterUrl = (): string => {
    const params = new URLSearchParams();

    if (redirectParam) {
      params.set('redirect', redirectParam);
    }

    if (emailParam) {
      params.set('email', emailParam);
    }

    const query = params.toString();

    return query ? `/register?${query}` : '/register';
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col lg:flex-row bg-[#F7F9FB] select-none text-[#1a1b4b]">

      {/* ============================================================
          LEFT PANEL: AUTHENTICATION FORM (40% on desktop)
          ============================================================ */}
      <div className="w-full lg:w-[40%] h-full p-6 sm:p-10 md:p-12 xl:p-14 flex flex-col justify-between bg-white overflow-y-auto z-10 flex-shrink-0 border-r border-[#3e4491]/20">
        {/* Center Form Section */}
        <div className="my-auto py-6 max-w-md w-full mx-auto space-y-6">
          <div>
            <Link
              to="/"
              className="inline-flex items-center group transition-transform hover:scale-[1.02] mb-6"
            >
              <Logo size="md" />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#1a1b4b]">
              Welcome back
            </h1>
            <p className="text-xs sm:text-sm text-[#3e4491] font-medium mt-1.5">
              Welcome back! Please enter your details to sign in.
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div
              role="alert"
              className="p-3.5 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-xs font-medium animate-in fade-in slide-in-from-top-1"
            >
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Address */}
            <div>
              <label
                htmlFor="login-email"
                className="block text-xs font-bold text-[#1a1b4b] mb-1.5"
              >
                Email
              </label>

              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />

                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full h-11 pl-10 pr-3.5 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm text-[#1a1b4b] focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] focus:bg-white transition shadow-2xs font-medium"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="login-password"
                className="block text-xs font-bold text-[#1a1b4b] mb-1.5"
              >
                Password
              </label>

              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />

                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full h-11 pl-10 pr-10 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm text-[#1a1b4b] focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] focus:bg-white transition shadow-2xs font-medium"
                />

                {/* Password Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#3e4491] hover:text-[#1a1b4b] transition focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Sign In Submit Button */}
            <button
              type="submit"
              disabled={
                isLoading ||
                !email.trim() ||
                !password.trim()
              }
              className="w-full h-11 mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-sm shadow-md shadow-[#1a1b4b]/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#f7a400]" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight className="h-4 w-4 text-[#f7a400]" />
                </>
              )}
            </button>
          </form>

          {/* Quick Demo Credentials */}
          <div className="pt-3 border-t border-[#3e4491]/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleDemoLogin}
                className="text-[#292a73] hover:text-[#3a9efd] hover:underline font-bold text-xs transition"
              >
                Fill Demo User
              </button>
              <span className="text-[#3e4491]/40">•</span>
              <button
                type="button"
                onClick={() => {
                  setEmail('adminconnect@connect.com');
                  setPassword('password123');
                }}
                className="text-[#292a73] hover:text-[#3a9efd] hover:underline font-bold text-xs transition"
              >
                Fill Platform Admin
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Account Creation Link */}
        <div className="pt-4 text-center">
          <p className="text-xs text-[#3e4491] font-medium">
            Don't have an account?{' '}
            <Link
              to={getRegisterUrl()}
              className="font-bold text-[#1a1b4b] hover:text-[#3a9efd] transition underline underline-offset-4 decoration-[#3a9efd]/60"
            >
              Sign up for free
            </Link>
          </p>
        </div>
      </div>

      {/* ============================================================
          RIGHT PANEL: SAHAJ CONNECT BRANDING HERO (60% on desktop)
          ============================================================ */}
      <div className="hidden lg:flex lg:w-[60%] h-full relative overflow-hidden flex-col p-10 xl:p-14 select-none">
        {/* Background Hero Image */}
        <img
          src="/login-page.png"
          alt="Sahaj Connect Team Collaboration"
          className="absolute inset-0 w-full h-full object-cover object-center transform scale-100 hover:scale-105 transition-transform duration-1000 ease-out"
        />

        {/* Content Wrapper */}
        <div className="relative z-10 flex flex-col h-full max-w-xl">
          {/* Top Brand Badges */}
          <div className="flex items-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-[#1a1b4b] text-[11px] font-bold shadow-sm">
              <span>Sahaj Connect Workspace</span>
            </div>

            <div className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1a1b4b] text-[11px] font-bold shadow-sm">
              <Globe className="h-3 w-3 text-[#3a9efd]" />
              <span>Multilingual AI Translation</span>
            </div>
          </div>

          {/* Hero Typography */}
          <div className="mb-6">
            <h2 className="text-3xl xl:text-4xl font-black text-white leading-tight mb-2">
              Communicate<br />
              Collaborate<br />
              <span className="text-[#6C5CE7]">Get things done</span>
            </h2>
            <p className="text-white/90 text-xs xl:text-sm font-medium max-w-md mt-3">
              A modern communication platform for your company built for your team.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="flex flex-col gap-2.5 max-w-[310px]">
            {/* Card 1 */}
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-2.5 rounded-2xl shadow-lg border border-white/20 transition-transform hover:-translate-y-1">
              <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-[#6C5CE7]" fill="currentColor" strokeWidth={0} />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold text-[#1a1b4b]">Team Messaging</span>
                <span className="text-[11px] text-[#1a1b4b]/60 font-medium leading-tight">Real-time conversations, <br /> channels and direct messages</span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-2.5 rounded-2xl shadow-lg border border-white/20 transition-transform hover:-translate-y-1">
              <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-[#3a9efd]" fill="currentColor" strokeWidth={0} />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold text-[#1a1b4b]">Collaborate</span>
                <span className="text-[11px] text-[#1a1b4b]/60 font-medium leading-tight">Share files, ideas and updates <br /> in one place</span>
              </div>
            </div>

            {/* Card 3 */}
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-2.5 rounded-2xl shadow-lg border border-white/20 transition-transform hover:-translate-y-1">
              <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center">
                <Video className="w-4 h-4 text-green-500" fill="currentColor" strokeWidth={0} />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold text-[#1a1b4b]">Meet & Connect</span>
                <span className="text-[11px] text-[#1a1b4b]/60 font-medium leading-tight">Instant calls and virtual meetings</span>
              </div>
            </div>

            {/* Card 4 */}
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-2.5 rounded-2xl shadow-lg border border-white/20 transition-transform hover:-translate-y-1">
              <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-orange-500" fill="currentColor" strokeWidth={0} />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold text-[#1a1b4b]">Stay Organized</span>
                <span className="text-[11px] text-[#1a1b4b]/60 font-medium leading-tight">Find everything, anytime</span>
              </div>
            </div>
          </div>
        </div>


      </div>

    </div>
  );
};